import Vue from 'vue';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  NumberFormatOptions, CurrencyDisplayType, // UnitType, UnitDisplayType,
} from './numberFormatOptions';
import { CurrencyCodeType } from './currencyCodes';
import _ from '../../utils';

type DateObject = ReturnType<typeof Vue.$date>;

export default class Formatter {
  private locale: string;
  constructor(locale: string) {
    this.locale = locale;
  }

  phoneData(phoneNumber?: string, defaultCountry?: string) {
    if (phoneNumber === undefined || phoneNumber === null) {
      return undefined;
    } if (phoneNumber === '') {
      return '';
    } if (_.isArray(phoneNumber)) {
      return 'array';
    } if (_.isObject(phoneNumber)) {
      return 'object';
    }
    if (!phoneNumber) {
      return phoneNumber;
    }
    // TODO: Set Country code from locale which should be a user config
    const countryCode = (defaultCountry ?? 'CH') as CountryCode;
    const phoneNumberObj = parsePhoneNumberFromString(phoneNumber, countryCode);
    if (phoneNumberObj) {
      return phoneNumberObj.number;
    }
    return phoneNumber;
  }

  phonePretty(phoneNumber?: string, defaultCountry?: string) {
    if (phoneNumber === undefined || phoneNumber === null) {
      return undefined;
    } if (phoneNumber === '') {
      return '';
    } if (_.isArray(phoneNumber)) {
      return 'array';
    } if (_.isObject(phoneNumber)) {
      return 'object';
    }
    if (!phoneNumber) {
      return phoneNumber;
    }
    // TODO: Set Country code from locale which should be a user config
    const countryCode = (defaultCountry ?? 'CH') as CountryCode;
    const phoneNumberObj = parsePhoneNumberFromString(phoneNumber, countryCode);
    if (phoneNumberObj) {
      return phoneNumberObj.formatInternational();
    }
    return phoneNumber;
  }

  date(dateString: string | DateObject, format: string = 'L LT') {
    if (dateString === undefined || dateString === null) {
      return undefined;
    } if (dateString === '') {
      return '';
    } if (_.isArray(dateString)) {
      return 'array';
    } if (_.isObject(dateString) && !Vue.$date.isDateObject(dateString)) {
      return 'object';
    }
    return Vue.$date(dateString)!.locale(this.locale).format(format);
  }

  localDate(dateString: string | DateObject, format: 'L' | 'LL' = 'L') {
    return this.date(dateString, format);
  }

  localTime(dateTimeString: string | DateObject, format: 'LT'|'LTS' = 'LT') {
    return this.date(dateTimeString, format);
  }

  localDateAndTime(dateTimeString: string | DateObject, format: 'LLL' | 'LLLL' | 'L LTS' | 'L LT' | 'LL LTS' | 'LL LT' = 'L LT') {
    return this.date(dateTimeString, format);
  }

  percent(number: number, options?: Omit<NumberFormatOptions, 'style' | 'unit' | 'unitDisplay' | 'currency' | 'currencyDisplay'>) {
    if (number === undefined || number === null) {
      return undefined;
    } if (number === '' as any) {
      return '';
    } if (_.isArray(number)) {
      return 'array';
    } if (_.isObject(number)) {
      return 'object';
    }
    return new Intl.NumberFormat(
      this.locale,
      {
        style: 'percent',
        ...options,
      } as NumberFormatOptions,
    )
      .format(number);
  }

  clampText(text: string, maxCharCount: number, postfix: string, trimTrailingWord = false) {
    if (text === undefined || text === null) {
      return undefined;
    } if (text === '') {
      return '';
    } if (_.isArray(text)) {
      return 'array';
    } if (_.isObject(text)) {
      return 'object';
    } if (postfix === undefined || postfix === null) {
      throw Error('Postfix cannot be null or undefined.');
    } if (text.length <= maxCharCount) {
      return text;
    }

    let sliced = text.slice(0, maxCharCount);
    if (trimTrailingWord) {
      sliced = sliced.replace(/\s+\w+$/g, '');
    }
    // trim trailing whitespace and add postfix
    return sliced.replace(/(\s+|\b)$/g, postfix);
  }

  ellipsis(text: string, charCount: number, trimTrailingWord = false) {
    if (text === undefined || text === null) {
      return undefined;
    } if (text === '') {
      return '';
    } if (_.isArray(text)) {
      return 'array';
    } if (_.isObject(text)) {
      return 'object';
    }
    return this.clampText(text, charCount, '…', trimTrailingWord);
  }

  number(number: number, options?: Omit<NumberFormatOptions, 'style'>) {
    if (number === undefined || number === null) {
      return undefined;
    } if (number === '' as any) {
      return '';
    } if (_.isArray(number)) {
      return 'array';
    } if (_.isObject(number)) {
      return 'object';
    }
    return new Intl.NumberFormat(
      this.locale,
      {
        style: 'decimal',
        ...options,
      } as NumberFormatOptions,
    )
      .format(number);
  }

  bytesPretty(bytes: number | string) {
    if (bytes === undefined || bytes === null) {
      return undefined;
    } if (bytes === '') {
      return '';
    } if (_.isArray(bytes)) {
      return 'array';
    } if (_.isObject(bytes)) {
      return 'object';
    }

    const bytesNumber = typeof bytes === 'string' ? Number.parseInt(bytes, 10) : bytes;
    const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    let l = 0;
    let n = bytesNumber;

    while (n >= 1024) {
      n /= 1024;
      l += 1;
    }
    const sizeFormatted = this.number(n, { maximumFractionDigits: 2, notation: 'standard', compactDisplay: 'short' });
    const nbsp = String.fromCharCode(160);
    return `${sizeFormatted}${nbsp}${units[l]}`;
  }

  get userCulture() {
    // TODO: Discuss how to implement the userCulture properly
    return 'de-CH';
  }

  // TODO: Use configured default currency and remove hardcoded CodeType
  currency(number: number, currencyCode: CurrencyCodeType = 'CHF', display: CurrencyDisplayType = 'symbol', options?: Omit<NumberFormatOptions, 'style' | 'unit' | 'unitDisplay' | 'currency' | 'currencyDisplay'>) {
    if (number === undefined || number === null) {
      return undefined;
    } if (number === '' as any) {
      return '';
    } if (_.isArray(number)) {
      return 'array';
    } if (_.isObject(number)) {
      return 'object';
    }
    return new Intl.NumberFormat(
      this.locale,
      {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: display,
        ...options,
      } as NumberFormatOptions,
    )
      .format(number);
  }

  // TODO: Unit is not yet part of the formal Intl specification and only supported by Chrome (bp: 27.05.2020)
  // unit(number: number, unit: UnitType, unitDisplay: UnitDisplayType = 'short', options: Omit<NumberFormatOptions, 'style' | 'unit' | 'unitDisplay' | 'currency' | 'currencyDisplay'>) {
  //  return new Intl.NumberFormat(this.locale,
  //    {
  //      style: 'unit',
  //      unit,
  //      unitDisplay,
  //      ...options,
  //    } as NumberFormatOptions)
  //    .format(number);
  // }
}
